/**
 * Shared odds conversion utilities
 * Used by both client and server for odds calculations
 */

/**
 * Convert decimal odds to American odds format
 * @example decimalToAmerican(2.5) => "+150"
 * @example decimalToAmerican(1.5) => "-200"
 */
export function decimalToAmerican(decimal: number): string {
  if (decimal <= 1) {
    // Edge case: decimal odds of 1.0 or less means zero/negative profit — treat as even
    return '+100';
  }
  if (decimal >= 2) {
    return `+${Math.round((decimal - 1) * 100)}`;
  }
  return Math.round(-100 / (decimal - 1)).toString();
}

/**
 * Normalize an American odds string to a consistent numeric format.
 * Handles AI hallucinations like "Even", "EVEN", "PK", mapping them to "+100".
 * Otherwise returns the trimmed value unchanged (parseInt handles a leading "+").
 * Returns null if input is null/undefined/empty, or is not a plain number ("-Infinity", "NaN").
 */
export function normalizeAmericanOdds(odds: string | null | undefined): string | null {
  if (!odds) return null;
  const trimmed = odds.trim();
  if (!trimmed) return null;
  if (/^even$/i.test(trimmed) || /^pk$/i.test(trimmed)) return '+100';
  return /^[+-]?\d+(\.\d+)?$/.test(trimmed) ? trimmed : null;
}

/** What every screen shows in place of odds when there is no usable price. */
export const MISSING_ODDS = '-';

/**
 * American odds for display: "+150", "-110", "+100" for "Even"/"PK", rounded to a whole price.
 * Null when there is no usable price (missing, unparseable or 0); screens show MISSING_ODDS.
 */
export function formatAmericanOdds(odds: string | number | null | undefined): string | null {
  const raw = typeof odds === 'number' ? odds : Number(normalizeAmericanOdds(odds ?? null));
  if (!Number.isFinite(raw)) return null;
  const rounded = Math.round(raw);
  if (rounded === 0) return null;
  return rounded > 0 ? `+${rounded}` : `${rounded}`;
}

/**
 * Convert American odds to decimal format
 * @example americanToDecimal("+150") => 2.5
 * @example americanToDecimal("-200") => 1.5
 */
export function americanToDecimal(american: string | number): number {
  const odds = typeof american === 'string' ? parseInt(american, 10) : american;
  // Invalid/zero odds have no decimal equivalent; fall back to even (2.0) rather
  // than returning NaN/Infinity, which would poison downstream payout math.
  if (!Number.isFinite(odds) || odds === 0) return 2.0;
  if (odds > 0) {
    return 1 + odds / 100;
  }
  return 1 + 100 / Math.abs(odds);
}

/** An American price as a number; null when missing, unparseable or 0. */
export function parseAmericanPrice(odds: string | null | undefined): number | null {
  const price = odds ? parseInt(odds, 10) : Number.NaN;
  return Number.isFinite(price) && price !== 0 ? price : null;
}

/**
 * Mean of American prices taken over decimal odds (+150 and -150 give +108; their raw mean, 0, is
 * not a price). Skips non-finite and zero prices; null when none are left.
 */
export function averageAmericanOdds(prices: readonly number[]): number | null {
  const valid = prices.filter((p) => Number.isFinite(p) && p !== 0);
  if (valid.length === 0) return null;
  const meanDecimal = valid.reduce((sum, p) => sum + americanToDecimal(p), 0) / valid.length;
  return Number(decimalToAmerican(meanDecimal));
}

/**
 * Calculate implied probability from American odds
 * @example impliedProbability("-150") => 0.6 (60%)
 * @example impliedProbability("+200") => 0.333 (33.3%)
 */
export function impliedProbability(american: string | number): number {
  const odds = typeof american === 'string' ? parseInt(american, 10) : american;
  // Invalid/zero odds: fall back to a coin flip rather than NaN.
  if (!Number.isFinite(odds) || odds === 0) return 0.5;
  if (odds > 0) {
    return 100 / (odds + 100);
  }
  return Math.abs(odds) / (Math.abs(odds) + 100);
}

/**
 * Lowest decimal price a book will post. At or below this, the quote is a boundary
 * placeholder meaning "this side is effectively certain" rather than a priced opinion.
 *
 * Every out-of-range `over_or_home_no_vig_prob` seen in production had one leg sitting exactly
 * here. On the low side a 0.5-threes over at 48.00 against 1.010, devigging to 0.021 when
 * the WNBA training corpus never went below 0.318. On the high side an alternate
 * first-half team total at 1.010 against 26.00, devigging to 0.963. Same signature both
 * ways, so one floor catches both.
 */
const ODDS_FLOOR_DECIMAL = 1.02;

/**
 * No-vig (devigged) probability of the OVER side, from two decimal prices.
 *
 * Returns null when the quote carries no market information: a missing or non-numeric
 * price, or either leg at the book's floor. Null rather than a clamped number on purpose,
 * so the caller records "unknown" and the model takes the NaN branch it was fitted with,
 * instead of a value far outside anything the corpus contained.
 *
 * Measured over 7 days of odds_snapshots: the floor rule drops 80 of 51,342 quotes
 * (0.16%), takes the minimum devigged probability from 0.021 to 0.196, and leaves 137
 * genuinely high-probability quotes untouched.
 */
export function noVigProbFromDecimalOdds(
  overPrice: string | number | null | undefined,
  underPrice: string | number | null | undefined,
): number | null {
  if (overPrice == null || underPrice == null) return null;

  const overDec = typeof overPrice === 'string' ? Number(overPrice) : overPrice;
  const underDec = typeof underPrice === 'string' ? Number(underPrice) : underPrice;
  if (!Number.isFinite(overDec) || !Number.isFinite(underDec)) return null;
  if (overDec <= ODDS_FLOOR_DECIMAL || underDec <= ODDS_FLOOR_DECIMAL) return null;

  const impliedOver = 1 / overDec;
  const impliedUnder = 1 / underDec;
  return impliedOver / (impliedOver + impliedUnder);
}

/**
 * The market's no-vig probability of the side a pick took. `over_or_home_no_vig_prob` stores the Over
 * side's, or the home side's for a spread, so an Under or away pick reads 1 - p (backlog 96).
 */
export function pickSideMarketProb(
  overOrHomeProb: number | null | undefined,
  side: string | null | undefined,
): number | null {
  if (overOrHomeProb == null || !side) return null;
  const s = side.toLowerCase();
  if (s === 'over' || s === 'home') return overOrHomeProb;
  if (s === 'under' || s === 'away') return 1 - overOrHomeProb;
  return null;
}

/** Shortest price a main line carries, as implied probability (about -233): sharp rows under it sat 0.09
 * to 0.34 points off the soft median, past it 0.80 and up (14 days of props to 2026-10-30). */
export const MAIN_LINE_MAX_IMPLIED_PROB = 0.7;

/** A line and its two decimal prices, as odds_snapshots stores them. */
export interface LineQuote {
  line: number | null;
  overPrice: number | null;
  underPrice: number | null;
}

/** A ladder with no balanced rung still leaves a one-sided, lopsided or thin rung (pickMainLine). */
export function isMainLine({ line, overPrice, underPrice }: LineQuote): boolean {
  if (line == null || overPrice == null || underPrice == null) return false;
  return Math.max(1 / overPrice, 1 / underPrice) <= MAIN_LINE_MAX_IMPLIED_PROB;
}

/** Whether a book's row counts as its quote: a sharp book's line only when it is a main line (the
 * exchanges store off-market ladder rungs); a moneyline has no line to be off. */
export function countsAsBookQuote(quote: LineQuote, isSharpBook: boolean): boolean {
  return !isSharpBook || quote.line == null || isMainLine(quote);
}

// =============================================================================
// Best Line Finder — Multi-book odds comparison
// =============================================================================

/**
 * Build a lookup key for a leg to find its comparison data.
 * Key format: "player|market|line|direction"
 * Used by both web and mobile to match legs against comparison maps.
 *
 * @returns Lookup key string, or null if required fields are missing.
 */
export function buildComparisonKey(
  player: string | undefined,
  marketKey: string | undefined,
  line: number | null | undefined,
  overUnder: string | null | undefined,
): string | null {
  if (!player || !marketKey || !overUnder) return null;
  return `${player}|${marketKey}|${line ?? 'null'}|${overUnder}`;
}

/** A single sportsbook's odds for a prop */
export interface BookOdds {
  sportsbook: string;
  odds: string; // American format: "-110"
  decimalOdds: number; // 1.909
  /** Sharp/reference book (Pinnacle, ProphetX, etc.): shown as context, never the "best bettable" pick. */
  isSharp?: boolean;
  /** ISO time the book last updated this market, from the odds feed (absent when the feed omits it). */
  lastUpdate?: string;
}

/** Comparison result for a single prop across all sportsbooks */
export interface BestOddsResult {
  player: string;
  market: string;
  line: number | undefined;
  direction: string; // "Over" | "Under" (or team name for h2h/spreads)
  currentBook: BookOdds; // User's selected sportsbook
  bestBook: BookOdds; // Best available
  allBooks: BookOdds[]; // All sportsbooks' odds
  hasBetterLine: boolean; // true if bestBook !== currentBook
}

/** Minimal bookmaker shape matching Odds API response (avoids importing server types) */
interface OddsApiOutcome {
  name: string;
  description?: string;
  price: number;
  point?: number;
}

interface OddsApiMarket {
  key: string;
  last_update?: string;
  outcomes: OddsApiOutcome[];
}

interface OddsApiBookmaker {
  key: string;
  title: string;
  markets: OddsApiMarket[];
}

interface OddsApiPropsData {
  bookmakers: OddsApiBookmaker[];
}

/**
 * Compare odds for all props across multiple bookmakers.
 * Finds the best available odds for each unique prop (player + market + line + direction).
 *
 * @param propsData - Odds API response with multiple bookmakers
 * @param userSportsbook - The user's selected sportsbook key (e.g. 'fanduel')
 * @returns Array of comparison results, one per unique prop
 *
 * @example
 * const results = findBestOdds(multiBookProps, 'fanduel');
 * results.forEach(r => {
 *   if (r.hasBetterLine) {
 *     console.log(`${r.player} ${r.direction} ${r.line} ${r.market}: Best at ${r.bestBook.sportsbook} (${r.bestBook.odds})`);
 *   }
 * });
 */
export function findBestOdds(
  propsData: OddsApiPropsData,
  userSportsbook: string,
  sharpBooks: ReadonlySet<string> = new Set(),
): BestOddsResult[] {
  if (!propsData?.bookmakers?.length) return [];

  // Build a map: propKey → { sportsbook → BookOdds }
  // propKey = "player|market|line|direction" to uniquely identify a prop
  const propMap = new Map<string, Map<string, BookOdds>>();

  for (const bookmaker of propsData.bookmakers) {
    for (const market of bookmaker.markets || []) {
      for (const outcome of market.outcomes || []) {
        const player = outcome.description || outcome.name;
        const direction = outcome.name; // "Over", "Under", or team name
        const line = outcome.point;
        const propKey = `${player}|${market.key}|${line ?? 'null'}|${direction}`;

        if (!propMap.has(propKey)) {
          propMap.set(propKey, new Map());
        }

        const decimal = outcome.price;
        const american = decimalToAmerican(decimal);

        propMap.get(propKey)!.set(bookmaker.key, {
          sportsbook: bookmaker.key,
          odds: american,
          decimalOdds: decimal,
          isSharp: sharpBooks.has(bookmaker.key),
          lastUpdate: market.last_update || undefined,
        });
      }
    }
  }

  // Convert map to BestOddsResult array
  const results: BestOddsResult[] = [];

  for (const [propKey, bookOddsMap] of propMap) {
    const [player, market, lineStr, direction] = propKey.split('|');
    const line = lineStr === 'null' ? undefined : parseFloat(lineStr);

    const currentBook = bookOddsMap.get(userSportsbook);
    if (!currentBook) continue; // Skip props not available at user's book

    // Find the best BETTABLE odds (highest decimal = biggest payout). Sharp/reference books are
    // context only and never win "best", so they are skipped here (they still appear in allBooks).
    let bestBook = currentBook;
    for (const bookOdds of bookOddsMap.values()) {
      if (!bookOdds.isSharp && bookOdds.decimalOdds > bestBook.decimalOdds) {
        bestBook = bookOdds;
      }
    }

    const allBooks = Array.from(bookOddsMap.values()).sort((a, b) => b.decimalOdds - a.decimalOdds);

    results.push({
      player,
      market,
      line,
      direction,
      currentBook,
      bestBook,
      allBooks,
      hasBetterLine: bestBook.sportsbook !== userSportsbook,
    });
  }

  return results;
}

/** Stalest book timestamp, so the data-age label never overstates freshness; null if none parse. */
export function oldestBookUpdate(books: readonly BookOdds[]): string | null {
  let oldest: string | null = null;
  let oldestMs = Infinity;
  for (const book of books) {
    if (!book.lastUpdate) continue;
    const ms = Date.parse(book.lastUpdate);
    if (Number.isNaN(ms) || ms >= oldestMs) continue;
    oldestMs = ms;
    oldest = book.lastUpdate;
  }
  return oldest;
}
